<template>
  <div class="px-2 lg:px-0 h-full">
    <div v-if="hasError" class="error-message-container">
        <p class="error-message">
          Error loading account data, please try reloading the page.
        </p>
    </div>
    <div v-else-if="!isLoading">
      <div v-if="userData.application_status === 'pending' || userData.application_status === 'accepted' || userData.application_status === 'reapplied'" class="mx-auto h-fit bg-gray-100 border border-black rounded-lg flex flex-col" ref="account">
        <h2 class="text-center text-2xl underline pt-1 lg:pt-2">Account</h2>
        <div class="mx-auto w-full flex flex-col">
          <div class="mx-auto justify-self-center px-1">
            <p><strong>Display Name</strong>: {{ userData.display_name }}</p>
            <p><strong>Application Status</strong>: {{ userData.application_status }}</p>
            <p><strong>Application Time</strong>: {{ formattedDate }}</p>
            <p v-if="userData.application_status == 'accepted'"><strong>Channel listed in home page</strong>: <input type="checkbox" v-model="visibleToggle"></p>
            <p v-if="userData.application_status == 'accepted'" class="text-xs leading-3 w-fit mx-auto">Note: Users inactive for several months will unlist.</p>
            <div class="flex flex-row max-w-full"><p><strong>Pronouns</strong>: </p><div class="flex flex-grow"><input type="text" class="py-0 w-full" v-model="pronouns"></div></div>
            <p class="text-xs leading-3 w-fit mx-auto">Ex. (He/Him, They/Them, She/Her, etc.)</p>
            <div class="flex flex-row max-w-full"><p><strong>Email</strong>: </p><div class="flex flex-grow"><input type="text" class="py-0 w-full" v-model="twitchEmail"></div></div>
            <p><strong>Receive emails from Peer2Peer</strong>: <input type="checkbox" v-model="emailAllowed"></p>
          </div>
          <div class="flex flex-col flex-grow pt-2">
            <div class="relative">
              <div class="tabcontainer border-b border-black" ref="tabContainer" @scroll="updateArrowVisibility">
                <div v-for="(group, label) in groupedTags" :key="label" class="relative max-w-xs inline-block lg:block" ref="dropdowns">
                    <button class="tab" :class="{ 'active': activeMenu.label === label }" @click="setActiveMenu(label, group)">
                        {{ label }}
                    </button>
                </div>
                <svg @click="scrollTabsLeft" class="cursor-pointer arrow top-1/2 left-[-2px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-show="showLeftArrow">
                  <rect x="4" y="4" width="16" height="16" stroke="black" stroke-width="1" fill="white"/>
                  <path d="M15 12H9M12 8L8 12L12 16" stroke="black" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg @click="scrollTabsRight" class="cursor-pointer arrow top-1/2 right-[-2px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-show="showRightArrow">
                    <rect x="4" y="4" width="16" height="16" stroke="black" stroke-width="1" fill="white"/>
                    <path d="M9 12h6M12 16l4-4-4-4" stroke="black" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
            <div class="relative flex-grow border-b border-black">
              <div class="static-tags flex h-full" ref="baseActiveMenu" @scroll="updateArrowVisibility">
                <div v-if="activeMenu" v-for="tag in activeMenu.group" :key="tag.title">
                    <button class="btn mx-1 px-1" :class="{ 'active': selectedTags[tag.slug]?.selected }" @click.prevent="toggleTag(tag)">
                      {{ tag.title }}
                      <span class="text-xs" v-if="selectedTags[tag.slug]?.importance">({{ selectedTags[tag.slug].importance }})</span>
                    </button>
                    <div class="flex flex-col" style="min-height: 125px;" v-if="selectedTags[tag.slug]?.selected">
                      <label class="text-xs text-center">Importance</label>
                      <v-select id="importance" class="text-black" v-model="selectedTags[tag.slug].importance" :options="importanceOptions"/>
                    </div>
                </div>
              </div>
              <svg @click="scrollTagsLeft" class="cursor-pointer arrow top-[18px] left-[-3px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-show="showTagsLeftArrow">
                  <rect x="4" y="4" width="16" height="16" stroke="black" stroke-width="1" fill="white"/>
                  <path d="M15 12H9M12 8L8 12L12 16" stroke="black" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <svg @click="scrollTagsRight" class="cursor-pointer arrow top-[18px] right-[-3px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-show="showTagsRightArrow">
                  <rect x="4" y="4" width="16" height="16" stroke="black" stroke-width="1" fill="white"/>
                  <path d="M9 12h6M12 16l4-4-4-4" stroke="black" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
        <div class="w-fit text-center text-xs mx-auto my-1 self-end">Importance 1 is bold and listed first, 2 is standard, 3 is hidden publicly but search will still see it.<br> All changes automatically saved.</div>
        <div v-if="userData.application_status == 'accepted'" class="w-full text-center mx-auto my-1 self-end border-black border-t-2">Join the discord at: <a href="https://discord.com/invite/h4VHumNVWT">https://discord.com/invite/h4VHumNVWT</a></div>
      </div>
      
      <div v-else-if="userData.application_status == 'denied'" class="mx-auto max-w-4xl p-4">
          <h2 class="text-center text-2xl text-white text-outline">Your account has been disabled.</h2>
      </div>
      <div v-else-if="userData.application_status == 'frozen'" class="mx-auto max-w-4xl p-4">
          <div class="flex flex-col items-center justify-center">
              <h2 class="text-center text-2xl text-white text-outline">Your account has been frozen. You should receive an email with more information.<br/> When you are ready and think you have met the requirements outlined in the email you are welcome to re-apply.</h2>
              <button class="mx-auto btn px-1" @click="reapply()">Reapply</button>
          </div>
      </div>
    </div>
  </div>
</template>

<script>
  import VueSelect from 'vue-select';
  import 'vue-select/dist/vue-select.css';
  import { debounce } from 'lodash';
  import _ from 'lodash';

  export default {
    name: 'Account',
    components: {
    VueSelect
  },
    data() {
      return {
        userData: {},
        tags: [],
        selectedTags: [],
        groupedTags: {},
        activeMenu: { label: '', group: [] },
        pronouns: null,
        twitchEmail: null,
        emailAllowed: false,
        visibleToggle: false,
        shouldTriggerUpdatePronouns: false,
        shouldTriggerUpdateTags: false,
        shouldTriggerUpdateEmail: false,
        shouldTriggerUpdateEmailAllowed: false,
        shouldTriggerUpdateVisible: false,
        arrowUpdateTrigger: 0,
        isLoading: true,
        importanceOptions: [1,2,3],
        hasError: false,
      }
    },
    async created() {
    },
    async mounted() {
      try {
        await Promise.all([this.getUserData(), this.getTags()]);
        // Uncomment the line below to simulate an error
        //throw new Error('Simulated error: Failed to fetch data');
      } catch (error) {
        this.hasError = true;
        console.error('Error fetching data:', error);
      } finally {
        this.populateSelectedTags();
        //this.setUserTags();
        this.isLoading = false;
        this.$nextTick(() => {
          this.updateArrowVisibility();
        });
      }
    },
    computed: {
      formattedDate() {
        if (!this.userData.application_status_time) {
          return 'none';
        }

        const date = new Date(Date.parse(this.userData.application_status_time));
        const formatter = new Intl.DateTimeFormat('en-US', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short',
        });

        return formatter.format(date);
      },
      isEmailValid() {
        // Regular expression for email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(this.twitchEmail);
      },
      showLeftArrow() {
          this.arrowUpdateTrigger;
          return this.$refs.tabContainer && this.$refs.tabContainer.scrollLeft > 0;
      },
      showRightArrow() {
          this.arrowUpdateTrigger;
          const tabContainer = this.$refs.tabContainer;
          return tabContainer && tabContainer.scrollWidth > tabContainer.clientWidth && tabContainer.scrollLeft < tabContainer.scrollWidth - tabContainer.clientWidth;
      },
      showTagsLeftArrow() {
          this.arrowUpdateTrigger;
          return this.$refs.baseActiveMenu && this.$refs.baseActiveMenu.scrollLeft > 0;
      },
      showTagsRightArrow() {
          this.arrowUpdateTrigger;
          const tabContainer = this.$refs.baseActiveMenu;
          const tolerance = 5;
          return tabContainer && tabContainer.scrollWidth > tabContainer.clientWidth && tabContainer.scrollLeft + tolerance < tabContainer.scrollWidth - tabContainer.clientWidth;
      },
    },
    watch: {
      twitchEmail: {
        handler: debounce(function (newVal, oldVal) {
          if (this.shouldTriggerUpdateEmail) {
        // Call the updatePronouns method only if the flag is true
            this.updateEmail();
          } else {
            // Reset the flag to true after the initial triggering
            this.shouldTriggerUpdateEmail = true;
          }
        }, 1000),
        immediate: false, // default is false, set to true if you want to trigger it immediately when the component is mounted
      },
      visibleToggle: {
        handler: function (newVal, oldVal) {
          if (this.shouldTriggerUpdateVisible) {
        // Call the updatePronouns method only if the flag is true
            this.updateVisible();
          } else {
            // Reset the flag to true after the initial triggering
            this.shouldTriggerUpdateVisible = true;
          }
        },
        immediate: false, // default is false, set to true if you want to trigger it immediately when the component is mounted
      },
      emailAllowed: {
        handler: function (newVal, oldVal) {
          if (this.shouldTriggerUpdateEmailAllowed) {
        // Call the updatePronouns method only if the flag is true
            if (newVal && !this.isEmailValid) {
              window.alert('Please enter a valid email address.');
              // Uncheck the email allowed checkbox
              this.$nextTick(() => {
                this.shouldTriggerUpdateEmailAllowed = false;
                this.emailAllowed = false;
              });
            }else{
              this.updateEmailAllowed();
            }
          } else {
            // Reset the flag to true after the initial triggering
            this.shouldTriggerUpdateEmailAllowed = true;
          }
        },
        immediate: false, // default is false, set to true if you want to trigger it immediately when the component is mounted
      },
      pronouns: {
        handler: debounce(function (newVal, oldVal) {
          if (this.shouldTriggerUpdatePronouns) {
        // Call the updatePronouns method only if the flag is true
            this.updatePronouns();
          } else {
            // Reset the flag to true after the initial triggering
            this.shouldTriggerUpdatePronouns = true;
          }
        }, 1000),
        immediate: false, // default is false, set to true if you want to trigger it immediately when the component is mounted
      },
      selectedTags: {
        handler: function(newVal, oldVal) {
          // Call the updateTags method whenever selectedTags data changes
          if (this.shouldTriggerUpdateTags) {
             // Call the updatePronouns method only if the flag is true
            this.updateTags();
          } else {
            // Reset the flag to true after the initial triggering
            this.shouldTriggerUpdateTags = true;
          }
        },
        deep: true // Set deep to true to watch changes to nested properties
      },
    },
    methods: {
      async getUserData() {
        try {
          const response = await fetch('/api/account');
          const data = await response.json();
          this.userData = data;

          if(this.userData.pronouns !== null){
            this.pronouns = this.userData.pronouns;
          }else{
            this.shouldTriggerUpdatePronouns = true;
          }

          if(this.userData.twitch_email !== null){
            this.twitchEmail = this.userData.twitch_email;
          }else{
            this.shouldTriggerUpdateEmail = true;
          }
          if(this.userData.email_allowed == false){
            this.shouldTriggerUpdateEmailAllowed = true;
          }else{
            this.emailAllowed = this.userData.email_allowed;
          }

          if(this.userData.visible == false){
            this.shouldTriggerUpdateVisible = true;
          }else{
            this.visibleToggle = this.userData.visible;
          }
        } catch (error) {
          console.error(error);
        }
      },
      async getTags() {
        try {
          const response = await fetch(`${process.env.VUE_APP_NODE_SERVER_BASE_URL}/terms`);
          if (response.ok) {
            const data = await response.json();
            this.tags = data.data.map((tag) => ({
              ...tag,
              importance: null,
              selected: false,
            }));

            const groupedTags = _.groupBy(this.tags, 'tag_category.label');
            for (const [label, group] of Object.entries(groupedTags)) {
              groupedTags[label] = _.sortBy(group, 'title');
            }

            // Sort the groupedTags object alphabetically by its keys (labels)
            const sortedGroupedTags = Object.fromEntries(
              Object.entries(groupedTags).sort(([a], [b]) => a.localeCompare(b))
            );
            this.groupedTags = sortedGroupedTags;

            const firstLabel = Object.keys(sortedGroupedTags)[0];
            this.activeMenu = { label: firstLabel, group: sortedGroupedTags[firstLabel] };
          } else {
            console.log('Error retrieving user data.');
          }
        } catch (error) {
          console.log(error);
        }
      },
      populateSelectedTags() {
        const selectedTags = {};
        const tags = this.tags;
        tags.forEach((tag) => {
          selectedTags[tag.slug] = {
            slug: tag.slug,
            importance: tag.importance,
            selected: false,
          };
        });
        this.selectedTags = selectedTags;

        // Iterate over user tags and update the corresponding objects in selectedTags
        const userTags = this.userData.tags;
        for (const userTag of userTags) {
          selectedTags[userTag.tag.slug].selected = true;
          selectedTags[userTag.tag.slug].importance = userTag.importance.value;
        }
      },
      setUserTags() {
        const userTags = this.userData.tags;
        const selectedTags = this.selectedTags;
        for (const userTag of userTags) {
          selectedTags[userTag.tag.slug].selected = true;
          selectedTags[userTag.tag.slug].importance = userTag.importance.value;
        }
      },
      async updateTags() {
        try {
          const selectedTags = Object.values(this.selectedTags).filter(tag => tag.selected);
          const response = await fetch('/api/account/updateTags', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            },
            body: JSON.stringify({
              tags: selectedTags
            })
          });
          if (response.ok) {
            this.flashBorderColor('green');
          } else {
            this.flashBorderColor('red');
            console.log('Error updating tags.');
          }
        } catch (error) {
          this.flashBorderColor('red');
          console.log(error);
        }
      },
      async updatePronouns() {
        try {
          const response = await fetch('/api/account/updatePronouns', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            },
            body: JSON.stringify({
              pronouns: this.pronouns
            })
          });
          if (response.ok) {
            this.flashBorderColor('green');
            this.userData.pronouns = this.pronouns;
          } else {
            this.flashBorderColor('red');
            console.log('Error updating pronouns.');
          }
        } catch (error) {
          this.flashBorderColor('red');
          console.log(error);
        }
      },
      async updateEmail() {
        try {
          const response = await fetch('/api/account/updateEmail', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            },
            body: JSON.stringify({
              twitchEmail: this.twitchEmail
            })
          });
          if (response.ok) {
            this.flashBorderColor('green');
            this.userData.twitch_email = this.twitchEmail;
          } else {
            this.flashBorderColor('red');
            console.log('Error updating Email.');
          }
        } catch (error) {
          this.flashBorderColor('red');
          console.log(error);
        }
      },
      async updateEmailAllowed() {
        try {
          const response = await fetch('/api/account/updateEmailAllowed', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            },
            body: JSON.stringify({
              emailAllowed: this.emailAllowed
            })
          });
          if (response.ok) {
            this.flashBorderColor('green');
            this.userData.email_allowed = this.emailAllowed;
          } else {
            this.flashBorderColor('red');
            console.log('Error updating Email allowed.');
          }
        } catch (error) {
          this.flashBorderColor('red');
          console.log(error);
        }
      },
      async updateVisible() {
        try {
          const response = await fetch('/api/account/updateVisible', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            },
            body: JSON.stringify({
              visible: this.visibleToggle
            })
          });
          if (response.ok) {
            this.flashBorderColor('green');
            this.userData.visible = this.visibleToggle;
          } else {
            this.flashBorderColor('red');
            console.log('Error updating visible.');
          }
        } catch (error) {
          this.flashBorderColor('red');
          console.log(error);
        }
      },
      setActiveMenu(label, group) {
            this.activeMenu = { label, group };
            this.$nextTick(() => {
              this.updateArrowVisibility();

            const tabContainer = this.$refs.baseActiveMenu;
            });
      },
      toggleTag(tag) {
        const slug = tag.slug;
        if (this.selectedTags[slug].selected) {
          // If the tag is already selected, unselect it
          this.selectedTags[slug].selected = false;
          this.selectedTags[slug].importance = null;
        } else {
          // If the tag is not selected, select it
          this.selectedTags[slug].selected = true;
        }
        // Set the shouldTriggerUpdateTags flag to true
        this.shouldTriggerUpdateTags = true;
      },
      async flashBorderColor(color) {
        const element = this.$refs.account;
        const originalBoxShadow = element.style.boxShadow;
        element.style.boxShadow = `inset 0 0 0 5px ${color}`;

        setTimeout(() => {
          element.style.boxShadow = originalBoxShadow;
        }, 500); // Duration of the color flash in milliseconds
      },
      reapply(){
        window.location = '/reapply';
      },
      scrollTabsRight() {
          const tabContainer = this.$refs.tabContainer;
          const scrollAmount = tabContainer.clientWidth;
          tabContainer.scrollLeft += scrollAmount;
          this.updateArrowVisibility();
      },
      scrollTabsLeft() {
          const tabContainer = this.$refs.tabContainer;
          const scrollAmount = tabContainer.clientWidth;
          tabContainer.scrollLeft -= scrollAmount;
          this.updateArrowVisibility();
      },
      scrollTagsRight() {
          const tabContainer = this.$refs.baseActiveMenu;
          const scrollAmount = tabContainer.clientWidth;
          tabContainer.scrollLeft += scrollAmount;
          this.updateArrowVisibility();
      },
      scrollTagsLeft() {
          const tabContainer = this.$refs.baseActiveMenu;
          const scrollAmount = tabContainer.clientWidth;
          tabContainer.scrollLeft -= scrollAmount;
          this.updateArrowVisibility();
      },
      updateArrowVisibility() {
        this.arrowUpdateTrigger++;
      },
    },
  };
</script>
<style scoped>
  strong{
    @apply underline;
  }
  p{
    @apply pb-1;
  }
  button.active {
      background-color: #007bff;
      color: #fff;
  }
  .btn {
        @apply text-center border border-black rounded-md bg-gray-100 text-gray-700 hover:bg-gray-300;
  }
  .tab {
      @apply border-x-2 border-t-2 border-black rounded-t-md bg-gray-100 mr-1 px-1 hover:bg-gray-300;
  }
  .tabcontainer {
      @apply overflow-x-auto whitespace-nowrap lg:flex lg:flex-wrap px-1 lg:px-2 text-xs lg:text-sm text-black;
  }
  .static-tags{
      @apply overflow-x-auto whitespace-nowrap lg:flex lg:flex-wrap px-1 lg:px-2 text-xs lg:text-sm py-2;
  }
  /* Hide scrollbar for Chrome, Safari, and Opera */
  .tabcontainer::-webkit-scrollbar {
    display: none;
  }
  .static-tags::-webkit-scrollbar {
    display: none;
  }
    /* Hide scrollbar for Firefox */
  .tabcontainer, .static-tags {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
  }
    /* For other browsers, hide the scrollbar but still make the container scrollable */
  .tabcontainer, .static-tags {
    overflow: -moz-scrollbars-none; /* Older Firefox */
    -webkit-overflow-scrolling: touch;
  }
  .arrow{
      @apply absolute transform -translate-y-1/2 w-6 h-6 text-gray-500 fill-current lg:hidden;
  }
  .error-message-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    min-height: 300px;
  }
  .error-message {
    font-size: 1.5rem;
    font-weight: bold;
    text-align: center;
  }
</style>
<style>
  #importance .vs__dropdown-option {
    padding-top: 1px;
    padding-bottom: 1px;
  }
  #importance .vs__dropdown-menu{
    min-width: min-content;
    line-height: .75rem;
    padding: 0px;
  }
</style>